import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

import { App } from './app.js';
import * as PARTICLES from './particle-system.js';
import * as MATH from './math.js';
import { DragControls } from 'three/examples/jsm/controls/DragControls.js';
import * as NOISE from './noise.js';


class ParticleProject extends App {
  #particleSystem_ = null;


    #campfireLight_ = null;
    #smokeMaterial_ = null;

    #dragControls_ = null;
    #fireAttractorSphere_ = null;
    #fireAttractor_ = null;
    #smokeEmitterParams_ = null;  // so we can update its spawn position on drag




  constructor() {
    super();
  }

  async onSetupProject(pane) {
    this.loadRGBE('./resources/skybox/moonless_golf_2k.hdr');
    

    // Create a ground plane
    const whiteSquareTexture = new THREE.TextureLoader().load('resources/textures/whitesquare.png');
    whiteSquareTexture.wrapS = THREE.RepeatWrapping;
    whiteSquareTexture.wrapT = THREE.RepeatWrapping;
    whiteSquareTexture.repeat.set(500, 500);
    whiteSquareTexture.anisotropy = 16;
    const groundGeo = new THREE.PlaneGeometry(500, 500);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0xFFFFFF,
      map: whiteSquareTexture,
      metalness: 0.5,
      roughness: 0.6,
    });
    const groundMesh = new THREE.Mesh(groundGeo, groundMat);
    groundMesh.rotation.x = -Math.PI / 2;
    groundMesh.receiveShadow = true;
    this.Scene.add(groundMesh);

    // Add a tree
    const glbLoader = new GLTFLoader();
    

    glbLoader.load('./resources/models/campfire-logs.glb', (gltf) => {
      gltf.scene.traverse(c => {
        c.castShadow = true;
        c.receiveShadow = true;
      });
 
      this.Scene.add(gltf.scene);
    });

    // Create a flickering light
    this.#campfireLight_ = new THREE.PointLight(0xF8B867, 100);
    this.#campfireLight_.position.set(0, 4, 0);
    this.#campfireLight_.castShadow = true;
    this.Scene.add(this.#campfireLight_);

    const helper = new THREE.PointLightHelper(this.#campfireLight_);
    //this.Scene.add(helper);


    // Creeate the particle system
    this.#createParticleSystem_();

  }

  async #createParticleSystem_() {
     
    const textureLoader = new THREE.TextureLoader();
    const fireTexture = textureLoader.load('./resources/textures/fire.png');
    const smokeTexture = textureLoader.load('./resources/textures/smoke.png');

    const vsh = await fetch('./resources/shaders/points-vsh.glsl');
    const fsh = await fetch('./resources/shaders/points-fsh.glsl');
    const vshText = await vsh.text();
    const fshText = await fsh.text();

    this.#particleSystem_ = new PARTICLES.ParticleSystem();

    this.#fireAttractorSphere_ = new THREE.Mesh(
        new THREE.SphereGeometry(0.2, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0x00ff00, wireframe: true })
    );
    this.Scene.add(this.#fireAttractorSphere_);


    // Fire
    {
      const sizeOverLife = new MATH.FloatInterpolant([
        { time: 0, value: 3 },
        { time: 0.25, value: 10 },
        { time: 2, value: 0 },
      ]);
  
      const twinkleOverLife = new MATH.FloatInterpolant([
        { time: 0, value: 0 },
        { time: 1, value: 0 },
      ]);
  
      const alphaOverLife = new MATH.FloatInterpolant([
        { time: 0, value: 0 },
        { time: 0.25, value: 1 },
        { time: 2, value: 0 },
      ]);
  
      const colourOverLife = new MATH.ColorInterpolant([
        { time: 0, value: new THREE.Color(0xFFFFC0) },
        { time: 1, value: new THREE.Color(0xFF0000) },
      ]);
  
      const fireMaterial = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          map: { value: fireTexture },
          sizeOverLife: { value: sizeOverLife.toTexture() },
          colourOverLife: { value: colourOverLife.toTexture(alphaOverLife) },
          twinkleOverLife: { value: twinkleOverLife.toTexture() },
          spinSpeed: { value: 0 },
          lightFactor: { value: 0 },
    
        },
        vertexShader: vshText,
        fragmentShader: fshText,
        transparent: true,
        depthWrite: false,
        depthTest: true,
          blending: THREE.CustomBlending,
        blendEquation: THREE.AddEquation,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
      });

      // position the draggable sphere
      this.#fireAttractorSphere_.position.set(0, 8, 0);
    
      // make an attractor and keep a reference
      this.#fireAttractor_ = new PARTICLES.ParticleAttractor();
      this.#fireAttractor_.position.copy(this.#fireAttractorSphere_.position);
      this.#fireAttractor_.intensity = 8;
      this.#fireAttractor_.radius = 8;


    
      // Make the sphere draggable (use Renderer getter)
      this.#dragControls_ = new DragControls(
        [this.#fireAttractorSphere_],
        this.Camera,
        this.Renderer.domElement
      );

      // If you use OrbitControls, temporarily disable them during drag:
      this.#dragControls_.addEventListener('dragstart', () => {
        if (this.OrbitControls) this.OrbitControls.enabled = false;
      });

      this.#dragControls_.addEventListener('drag', () => {
          const s = this.#fireAttractorSphere_;

          this.#fireAttractor_.position.copy(s.position);
          this.#smokeEmitterParams_.shape.position.copy(s.position);
      });

      this.#dragControls_.addEventListener('dragend', () => {
        if (this.OrbitControls) this.OrbitControls.enabled = true;
      });
  
      const fireEmitterParams = new PARTICLES.EmitterParams();
      fireEmitterParams.shape = new PARTICLES.PointShape();
      fireEmitterParams.shape.positionRadiusVariance = 1;
      fireEmitterParams.maxLife = 2;
      fireEmitterParams.maxParticles = 300;
      fireEmitterParams.emissionRate = 100;
      fireEmitterParams.maxEmission = Number.MAX_SAFE_INTEGER;
      fireEmitterParams.velocityMagnitude = 4;
      fireEmitterParams.rotationAngularVariance = Math.PI / 6;
      fireEmitterParams.gravity = false;
      fireEmitterParams.spinSpeed = Math.PI / 2;
      fireEmitterParams.attractors.push(this.#fireAttractor_);

    
      const fireRendererParams = new PARTICLES.ParticleRendererParams();
      fireRendererParams.maxParticles = fireEmitterParams.maxParticles;
  
      fireEmitterParams.renderer = new PARTICLES.ParticleRenderer();
      fireEmitterParams.renderer.initialize(fireMaterial, fireRendererParams);
  
      const fireEmitter = new PARTICLES.Emitter(fireEmitterParams);
  
      this.#particleSystem_.addEmitter(fireEmitter);
  
      this.Scene.add(fireRendererParams.group);
    }

    // Smoke
    {
      const sizeOverLife = new MATH.FloatInterpolant([
        { time: 0, value: 8 },
        { time: 6, value: 24 },
      ]);
  
      const twinkleOverLife = new MATH.FloatInterpolant([
        { time: 0, value: 0 },
        { time: 1, value: 0 },
      ]);
  
      const alphaOverLife = new MATH.FloatInterpolant([
        { time: 0, value: 0 },
        { time: 0.5, value: 0.85 },
        { time: 6, value: 0 },
      ]);
  
      const colourOverLife = new MATH.ColorInterpolant([
        { time: 0, value: new THREE.Color(0xC0C0C0) },
        { time: 6, value: new THREE.Color(0x404040) },
      ]);
  
      const smokeMaterial = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          map: { value: smokeTexture },
          sizeOverLife: { value: sizeOverLife.toTexture() },
          colourOverLife: { value: colourOverLife.toTexture(alphaOverLife) },
          twinkleOverLife: { value: twinkleOverLife.toTexture() },
          spinSpeed: { value: 0 },
          lightFactor: { value: 1.0 },
          lightIntensity: { value: 2.0 },
        },
        vertexShader: vshText,
        fragmentShader: fshText,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        blending: THREE.CustomBlending,
        blendEquation: THREE.AddEquation,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
      });

      this.#smokeMaterial_ = smokeMaterial;
      console.log(this.#smokeMaterial_)
  
      this.#smokeEmitterParams_ = new PARTICLES.EmitterParams();
      this.#smokeEmitterParams_.shape = new PARTICLES.PointShape();
      this.#smokeEmitterParams_.shape.position.set(0, 8, 0);
      this.#smokeEmitterParams_.shape.positionRadiusVariance = 1;
      this.#smokeEmitterParams_.maxLife = 6;
      this.#smokeEmitterParams_.maxParticles = 500;
      this.#smokeEmitterParams_.emissionRate = 40;
      this.#smokeEmitterParams_.maxEmission = Number.MAX_SAFE_INTEGER;
      this.#smokeEmitterParams_.velocityMagnitude = 4;
      this.#smokeEmitterParams_.rotationAngularVariance = Math.PI / 8;
      this.#smokeEmitterParams_.gravity = false;
      this.#smokeEmitterParams_.spinSpeed = Math.PI / 8;
      this.#smokeEmitterParams_.dragCoefficient = 0.25;

      this.#smokeEmitterParams_.shape.position.copy(this.#fireAttractorSphere_.position);
  
      const smokeRendererParams = new PARTICLES.ParticleRendererParams();
      smokeRendererParams.maxParticles = this.#smokeEmitterParams_.maxParticles;
  
      this.#smokeEmitterParams_.renderer = new PARTICLES.ParticleRenderer();
      this.#smokeEmitterParams_.renderer.initialize(smokeMaterial, smokeRendererParams);
  
      const fireEmitter = new PARTICLES.Emitter(this.#smokeEmitterParams_);
  
      this.#particleSystem_.addEmitter(fireEmitter);
  
      this.Scene.add(smokeRendererParams.group);
    }
  }

  onStep(timeElapsed, totalTime) {
    if (this.#particleSystem_) {
      this.#particleSystem_.step(timeElapsed, totalTime);
    }

    
      if (this.#campfireLight_) {
          const noiseValue = NOISE.noise1D(totalTime * 2);
        
          const intensity = MATH.remap(-1, 1, 15, 50, noiseValue);
          this.#campfireLight_.intensity = intensity;

          if (this.#smokeMaterial_) {
            this.#smokeMaterial_.uniforms.lightIntensity.value = MATH.remap(
                -1, 1, 0.75, 2.0, noiseValue);
          }
      }
  }
}

let APP_ = new ParticleProject();

window.addEventListener('DOMContentLoaded', async () => {
  await APP_.initialize();
});
